import React, { MouseEventHandler } from "react";
import { NavLink, To } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BrandMark, Wordmark } from "./Brand";
import { FloatingChat } from "./Chat";
import { Ocultable } from "./Ocultable";

const NavItem = ({
  to,
  children,
  onClick,
}: {
  to: To;
  children: React.ReactNode;
  onClick: MouseEventHandler<HTMLAnchorElement>;
}) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) => `block px-3 py-2 rounded-xl ${isActive ? "bg-white/5 text-indigo-300" : "hover:bg-white/5"}`}
  >
    {children}
  </NavLink>
);

const Header = () => {
  const [open, setOpen] = React.useState(false);
  const link = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-xl transition hover:bg-white/5 ${isActive ? "text-indigo-300" : "text-slate-200"
    }`;

  return (
    <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-[#1f2637]/70">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2">
            <BrandMark className="h-8" />
            <span className="sr-only">Velasco Software</span>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" className={link} end>Inicio</NavLink>
            <NavLink to="/servicios" className={link}>Servicios</NavLink>
            <NavLink to="/sobre-nosotros" className={link}>Sobre nosotros</NavLink>
            <NavLink to="/contacto" className={link}>Contacto</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <a href="#contacto" className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
              <span>Hablemos</span>
              <ArrowRight className="w-4 h-4" />
            </a>
            <button className="md:hidden px-3 py-2 rounded-xl hover:bg-white/5" onClick={() => setOpen((v) => !v)} aria-label="Abrir menú">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" /></svg>
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#1f2637]">
          <div className="max-w-6xl mx-auto px-4 py-2 space-y-1">
            <NavItem to="/" onClick={() => setOpen(false)}>Inicio</NavItem>
            <NavItem to="/servicios" onClick={() => setOpen(false)}>Servicios</NavItem>
            <NavItem to="/sobre-nosotros" onClick={() => setOpen(false)}>Sobre nosotros</NavItem>
            <NavItem to="/contacto" onClick={() => setOpen(false)}>Contacto</NavItem>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-white/10 mt-16">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <Wordmark />
        <p className="text-sm text-slate-400">© {new Date().getFullYear()} Velasco Software. Todos los derechos reservados.</p>
      </div>
    </div>
  </footer>
);

export const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-[#1f2637] text-slate-200 font-sans">
    <Header />
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">{children}</main>
    <Ocultable enabled={true}>
      <FloatingChat />
    </Ocultable>
    <Footer />
  </div>
);
